import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/react';
import { Portal } from '@chakra-ui/react';
import {
  BlockQuoteIcon,
  Bold,
  BulletedList,
  CalloutIcon,
  ChevronDownIcon,
  CommmentTwoIcon,
  HeadingOneIcon,
  HeadingThreeIcon,
  HeadingTwoIcon,
  Italic,
  LinkIcon,
  NumberedList,
  StrikethroughIcon,
  TaskIcon,
  TextIcon,
  Underline,
} from '@sketchnote/icons';
import { IconTextBtn } from '@sketchnote/shared-components';
import {
  BalloonToolbar,
  ELEMENT_CALLOUT_TIP,
  ELEMENT_MICRO_TASK,
  useComments,
  useSlatePluginsStore,
} from '@sketchnote/slate-plugins';
import {
  ELEMENT_BLOCKQUOTE,
  ELEMENT_DEFAULT,
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_OL,
  ELEMENT_TODO_LI,
  ELEMENT_UL,
  MARK_BG_COLOR,
  MARK_BOLD,
  MARK_COLOR,
  MARK_ITALIC,
  MARK_STRIKETHROUGH,
  MARK_UNDERLINE,
  usePlateEditorRef,
} from '@udecode/plate';
import * as React from 'react';
import { ReactEditor } from 'slate-react';

import { ToolbarElement } from '../toolbar-element';
import { ToolbarLink } from '../toolbar-link';
import { ToolbarList } from '../toolbar-list';
import { ToolbarMark } from '../toolbar-mark';
import { ToolbarMarkTextColor } from '../toolbar-mark-text-color';

const { useState } = React;

export interface SelectionToolbarProps {
  hide?: {
    changeBlock?: boolean;
    changeColor?: boolean;
    changeBgColor?: boolean;
    comment?: boolean;
  };
}

export const SelectionToolbar = ({ hide }: SelectionToolbarProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [isColorOpen, setIsColorOpen] = useState(false);
  const [isHighlightOpen, setIsHighlightOpen] = useState(false);
  const editor = usePlateEditorRef();
  const setActiveComment = useComments((s) => s.setActiveComment);
  const setHideSelectionTool = useSlatePluginsStore((s) => s.setHideSelectionTool);
  const toggleMenu = (value?: boolean) => () => {
    setIsOpen(value ?? !isOpen);
  };

  const toggleColorMenu = (value?: boolean) => () => {
    setIsColorOpen(value ?? !isOpen);
  };
  const toggleHighlightMenu = (value?: boolean) => () => {
    setIsHighlightOpen(value ?? !isOpen);
  };
  const handleOnNewCommentThread = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    const domSelection = window.getSelection();
    if (!domSelection || domSelection.rangeCount < 1) return;

    const domRange = domSelection.getRangeAt(0);
    if (editor?.selection) {
      setActiveComment({ range: domRange, selection: editor?.selection });
    }
    setHideSelectionTool(true);
    editor && ReactEditor.deselect(editor);
  };

  return (
    <BalloonToolbar>
      <div
        className="bg-white px-12 flex py-4 mb-16"
        style={{
          color: '#55585E',
          border: 'none',
          boxShadow: '0px 2px 8px rgba(19, 23, 32, 0.16)',
        }}
      >
        {!hide?.changeBlock && (
          <>
            <Popover
              placement="bottom"
              isOpen={isOpen}
              onOpen={toggleMenu(true)}
              onClose={toggleMenu(false)}
              modifiers={[{ name: 'eventListeners', options: { scroll: isOpen } }]}
            >
              <PopoverTrigger>
                <IconTextBtn
                  className="p-5"
                  color="gray"
                  iconRight={<ChevronDownIcon wrapperClass="ml-12" />}
                >
                  Text
                </IconTextBtn>
              </PopoverTrigger>
              <Portal>
                <PopoverContent>
                  <div className="flex flex-col p-12 rounded-lg bg-white shadow-md text-gray-600">
                    <ToolbarElement
                      type={ELEMENT_DEFAULT}
                      tooltip={{ content: 'Heading 1 Ctrl+1', placement: 'right', arrow: false }}
                      icon={<TextIcon />}
                      label="Text"
                      onSelect={toggleMenu(false)}
                    />
                    <ToolbarElement
                      type={ELEMENT_H1}
                      tooltip={{ content: 'Heading 1 Ctrl+1', placement: 'right', arrow: false }}
                      icon={<HeadingOneIcon />}
                      label="Heading 1"
                      onSelect={toggleMenu(false)}
                    />
                    <ToolbarElement
                      type={ELEMENT_H2}
                      tooltip={{ content: 'Heading 2 Ctrl+2', placement: 'right', arrow: false }}
                      icon={<HeadingTwoIcon />}
                      label="Heading 2"
                      onSelect={toggleMenu(false)}
                    />
                    <ToolbarElement
                      type={ELEMENT_H3}
                      tooltip={{ content: 'Heading 3 Ctrl+3', placement: 'right', arrow: false }}
                      icon={<HeadingThreeIcon />}
                      label="Heading 3"
                      onSelect={toggleMenu(false)}
                    />
                    <ToolbarElement
                      type={ELEMENT_BLOCKQUOTE}
                      icon={<IconTextBtn iconLeft={<BlockQuoteIcon />} />}
                      tooltip={{ content: 'Quote Ctrl+6', placement: 'right', arrow: false }}
                      label="Quote"
                      onSelect={toggleMenu(false)}
                    />
                    <ToolbarElement
                      type={ELEMENT_CALLOUT_TIP}
                      icon={<IconTextBtn iconLeft={<CalloutIcon />} />}
                      tooltip={{ content: 'Add Callout Ctrl+8', placement: 'right', arrow: false }}
                      label="Callout tip"
                      onSelect={toggleMenu(false)}
                    />
                    <ToolbarElement
                      type={ELEMENT_MICRO_TASK}
                      icon={<IconTextBtn iconLeft={<CalloutIcon />} />}
                      tooltip={{
                        content: 'Add Microtask Ctrl+8',
                        placement: 'right',
                        arrow: false,
                      }}
                      label="Task"
                      onSelect={toggleMenu(false)}
                    />
                    <ToolbarElement
                      type={ELEMENT_TODO_LI}
                      icon={<IconTextBtn iconLeft={<TaskIcon />} />}
                      tooltip={{
                        content: 'Add Microtask Ctrl+8',
                        placement: 'right',
                        arrow: false,
                      }}
                      label="Todo List"
                      onSelect={toggleMenu(false)}
                    />
                    <ToolbarList
                      type={ELEMENT_UL}
                      icon={<IconTextBtn iconLeft={<BulletedList />} />}
                      tooltip={{
                        content: 'Add Microtask Ctrl+8',
                        placement: 'right',
                        arrow: false,
                      }}
                      label="Bullet list"
                      onSelect={toggleMenu(false)}
                    />
                    <ToolbarList
                      type={ELEMENT_OL}
                      icon={<IconTextBtn iconLeft={<NumberedList />} />}
                      tooltip={{
                        content: 'Add Microtask Ctrl+8',
                        placement: 'right',
                        arrow: false,
                      }}
                      label="Ordered list"
                      onSelect={toggleMenu(false)}
                    />
                  </div>
                </PopoverContent>
              </Portal>
            </Popover>
            <div className=" border-r mx-10"></div>
          </>
        )}
        <ToolbarMark
          type={MARK_BOLD}
          icon={<Bold />}
          tooltip={{ content: 'Bold Ctrl+B', placement: 'bottom', arrow: false }}
        />
        <ToolbarMark
          type={MARK_ITALIC}
          icon={<Italic />}
          tooltip={{ content: 'Italic Ctrl+I', placement: 'bottom', arrow: false }}
        />
        <ToolbarMark
          type={MARK_STRIKETHROUGH}
          icon={<StrikethroughIcon width={15} height={18} />}
          tooltip={{ content: 'Strikethrough Ctrl+S', placement: 'bottom', arrow: false }}
        />
        <ToolbarMark
          type={MARK_UNDERLINE}
          icon={<Underline />}
          tooltip={{ content: 'Underline Ctrl+U', placement: 'bottom', arrow: false }}
        />
        <ToolbarLink
          icon={<LinkIcon />}
          tooltip={{ content: 'Link Ctrl+L', placement: 'bottom', arrow: false }}
        />

        <div className=" border-r mx-10"></div>
        {!hide?.changeColor && (
          <>
            <Popover
              placement="bottom"
              isOpen={isColorOpen}
              onOpen={toggleColorMenu(true)}
              onClose={toggleColorMenu(false)}
            >
              <PopoverTrigger>
                <IconTextBtn
                  className="p-5"
                  color="gray"
                  iconRight={<ChevronDownIcon wrapperClass="ml-12" />}
                >
                  Color
                </IconTextBtn>
              </PopoverTrigger>
              <Portal>
                <PopoverContent>
                  <div className="flex flex-col p-12 rounded-lg bg-white shadow-md">
                    {colors.map((color, index) => (
                      <ToolbarMarkTextColor
                        key={`${color.label}-${index}`}
                        color={color.color}
                        type={MARK_COLOR}
                        onClick={toggleColorMenu(false)}
                        tooltip={{ content: 'Heading 1 Ctrl+1', placement: 'right', arrow: false }}
                        icon={
                          <span
                            className="w-14 h-14 rounded-full"
                            style={{ backgroundColor: color.bgColor }}
                          ></span>
                        }
                        label={color.label}
                      />
                    ))}
                  </div>
                </PopoverContent>
              </Portal>
            </Popover>
          </>
        )}
        {!hide?.changeBgColor && (
          <>
            <div className=" border-r mx-10"></div>
            <Popover
              placement="bottom"
              isOpen={isHighlightOpen}
              onOpen={toggleHighlightMenu(true)}
              onClose={toggleHighlightMenu(false)}
            >
              <PopoverTrigger>
                <IconTextBtn
                  className="p-5"
                  color="gray"
                  iconRight={<ChevronDownIcon wrapperClass="ml-12" />}
                >
                  Highlight
                </IconTextBtn>
              </PopoverTrigger>
              <Portal>
                <PopoverContent>
                  <div className="flex flex-col p-12 rounded-lg bg-white shadow-md">
                    {bgColors.map((color, index) => (
                      <ToolbarMarkTextColor
                        key={`${color.label}-${index}`}
                        color={color.color}
                        type={MARK_BG_COLOR}
                        tooltip={{ content: 'Heading 1 Ctrl+1', placement: 'right', arrow: false }}
                        onClick={toggleHighlightMenu(false)}
                        icon={
                          <span
                            className="w-14 h-14 rounded-full border"
                            style={{
                              backgroundColor: color.bgColor,
                              borderColor: color.borderColor,
                            }}
                          ></span>
                        }
                        label={color.label}
                      />
                    ))}
                  </div>
                </PopoverContent>
              </Portal>
            </Popover>
          </>
        )}
        {!hide?.comment && (
          <>
            <div className=" border-r mx-10"></div>
            <IconTextBtn
              className="p-5"
              color="gray"
              onClick={handleOnNewCommentThread}
              iconLeft={<CommmentTwoIcon className="mr-12" />}
            >
              Comment
            </IconTextBtn>
          </>
        )}
      </div>
    </BalloonToolbar>
  );
};

const colors = [
  { label: 'Default', color: 'Default', bgColor: '#131720' },
  { label: 'Green', color: '#2BC545', bgColor: '#2BC545' },
  { label: 'Yellow', color: '#FFD321', bgColor: '#FFD321' },
  { label: 'Orange', color: '#FF9700', bgColor: '#FF9700' },
  { label: 'Red', color: '#F55540', bgColor: '#F55540' },
  { label: 'Pink', color: '#FF3F72', bgColor: '#FF3F72' },
  { label: 'Purple', color: '#9641D0', bgColor: '#9641D0' },
  { label: 'Blue', color: '#627FE5', bgColor: '#627FE5' },
];

const bgColors = [
  { label: 'Default', color: 'Default', bgColor: '#F6F7F9', borderColor: '#878A92' },
  { label: 'Green', color: '#DFF8E3', bgColor: '#DFF8E3', borderColor: '#2BC545' },
  { label: 'Yellow', color: '#FFF7D6', bgColor: '#FFF7D6', borderColor: '#FFD321' },
  { label: 'Orange', color: '#FFE6BF', bgColor: '#FFE6BF', borderColor: '#FF9700' },
  { label: 'Red', color: '#FEEFED', bgColor: '#FEEFED', borderColor: '#F4381F' },
  { label: 'Pink', color: '#FEDEE6', bgColor: '#FEDEE6', borderColor: '#FF7197' },
  { label: 'Purple', color: '#F1E2FF', bgColor: '#F1E2FF', borderColor: '#9641D0' },
  { label: 'Blue', color: '#EEF1FC', bgColor: '#EEF1FC', borderColor: '#254DDA' },
];
